import { useState, useEffect } from 'react';
import { Download, ArrowRight, BookOpen, Clock, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { NewsletterForm } from '../components/forms/NewsletterForm';
import { LeadCaptureModal } from '../components/forms/LeadCaptureModal';
import { SEO } from '../components/SEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WritingPage() {
  const [articles, setArticles] = useState([]);
  const [whitepapers, setWhitepapers] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('All');
  const [selectedWhitepaper, setSelectedWhitepaper] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const topics = ['All', 'AI', 'LLM', 'CloudArchitecture', 'Kubernetes', 'DevSecOps', 'SRE', 'CostOptimization', 'IncidentResponse', 'Forensics', 'MLOps', 'Observability', 'Security'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesRes, whitepapersRes] = await Promise.all([
          axios.get(`${API}/articles`),
          axios.get(`${API}/whitepapers`),
        ]);
        setArticles(articlesRes.data);
        setWhitepapers(whitepapersRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleDownloadClick = (whitepaper) => {
    setSelectedWhitepaper(whitepaper);
    setIsModalOpen(true);
  };

  const filteredArticles = selectedTopic === 'All'
    ? articles
    : articles.filter((article) => article.topics.includes(selectedTopic));

  const featuredArticle = articles.find((a) => a.is_featured);

  return (
    <main className="pt-20" data-testid="writing-page">
      <SEO
        title="Cloud Infrastructure Research & Articles | Technical Insights"
        description="Read technical articles and research on cloud architecture, AI infrastructure, Kubernetes, DevSecOps, and cost optimization. Download whitepapers and subscribe to insights newsletter."
        keywords="cloud architecture articles, AI infrastructure blog, Kubernetes tutorials, DevSecOps best practices, cloud cost optimization, technical whitepapers"
        canonicalUrl="/writing"
      />
      {/* Header */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              Insights
            </p>
            <h1 className="font-['Montserrat'] font-extrabold text-4xl md:text-5xl text-[#2C3E50] mb-4">
              Research & Writing
            </h1>
            <p className="text-lg text-[#64748B] leading-relaxed">
              Strategic analysis and practical guides for enterprise infrastructure leaders.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Article - Smaller Size */}
      {featuredArticle && (
        <section className="py-8 bg-[#F8F9FA]">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            <div className="bg-white rounded-xl overflow-hidden border border-slate-200 card-hover" data-testid="featured-article">
              <div className="grid grid-cols-1 md:grid-cols-5">
                <div className="relative h-[200px] md:h-auto md:col-span-2">
                  <img
                    src={featuredArticle.thumbnail_url}
                    alt={featuredArticle.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#3498DB] text-white text-xs font-semibold px-3 py-1 rounded-full">
                      FEATURED RESEARCH
                    </span>
                  </div>
                </div>
                <div className="p-6 md:col-span-3">
                  <div className="flex items-center gap-4 text-sm text-[#64748B] mb-3">
                    <span>{featuredArticle.published_date}</span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {featuredArticle.read_time}
                    </span>
                  </div>
                  <h2 className="font-['Montserrat'] font-bold text-lg md:text-xl text-[#2C3E50] mb-3">
                    {featuredArticle.title}
                  </h2>
                  <p className="text-[#64748B] text-sm mb-4 leading-relaxed line-clamp-2">
                    {featuredArticle.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {featuredArticle.topics.slice(0, 3).map((topic, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-[#F8F9FA] text-[#64748B] px-3 py-1 rounded-full"
                      >
                        #{topic}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {featuredArticle.external_url && (
                      <a
                        href={featuredArticle.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 text-[#3498DB] font-medium hover:gap-3 transition-all duration-200"
                      >
                        {featuredArticle.external_url.includes('drive.google.com') ? 'View PDF' : 'Read Article'} <ArrowRight size={16} />
                      </a>
                    )}
                    {whitepapers[0] && (
                      <Button
                        onClick={() => handleDownloadClick(whitepapers[0])}
                        variant="outline"
                        size="sm"
                        className="border-[#2C3E50] text-[#2C3E50] hover:bg-[#2C3E50] hover:text-white"
                        data-testid="featured-download-pdf"
                      >
                        <Download size={14} className="mr-2" />
                        Download PDF
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Newsletter & External Writing */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Newsletter */}
            <div className="bg-[#F8F9FA] rounded-xl p-8" data-testid="newsletter-section">
              <h3 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50] mb-4">
                Newsletter
              </h3>
              <p className="text-[#64748B] mb-6">
                Practical insights on cloud infrastructure, AI/ML deployment, and enterprise architecture.
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  'Multi-cloud strategy and cost optimization',
                  'AI/LLM infrastructure trends',
                  'DevSecOps and SRE best practices',
                  'Technology landscape analysis',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-[#64748B]">
                    <BookOpen size={14} className="text-[#3498DB]" />
                    {item}
                  </li>
                ))}
              </ul>
              <NewsletterForm />
              <p className="text-xs text-[#64748B] mt-4">
                Published on Substack. No spam, unsubscribe anytime.
              </p>
            </div>

            {/* External Writing */}
            <div className="space-y-6">
              <h3 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50]">
                Writing on Other Platforms
              </h3>
              <div className="space-y-4">
                <a
                  href="https://siddharthkaul.substack.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-[#F8F9FA] rounded-lg hover:bg-slate-100 transition-colors duration-200"
                  data-testid="external-substack"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#FF6719]/10 flex items-center justify-center">
                      <BookOpen className="text-[#FF6719]" size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-[#2C3E50]">Substack</p>
                      <p className="text-sm text-[#64748B]">Weekly insights and analysis</p>
                    </div>
                  </div>
                  <ExternalLink size={18} className="text-[#64748B]" />
                </a>

                <a
                  href="https://linkedin.com/in/siddharth-k-8b41b619"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-[#F8F9FA] rounded-lg hover:bg-slate-100 transition-colors duration-200"
                  data-testid="external-linkedin"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0077B5]/10 flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#0077B5]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-[#2C3E50]">LinkedIn Articles</p>
                      <p className="text-sm text-[#64748B]">Professional insights and updates</p>
                    </div>
                  </div>
                  <ExternalLink size={18} className="text-[#64748B]" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topic Filters & Article List */}
      <section className="py-16 bg-[#F8F9FA]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <h3 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50] mb-6">
            Browse by Topic
          </h3>
          
          {/* Topic Filters */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="topic-filters">
            {topics.map((topic) => (
              <button
                key={topic}
                onClick={() => setSelectedTopic(topic)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  selectedTopic === topic
                    ? 'bg-[#3498DB] text-white'
                    : 'bg-white text-[#64748B] hover:bg-slate-100'
                }`}
                data-testid={`topic-filter-${topic.toLowerCase()}`}
              >
                {topic === 'All' ? 'All Articles' : `#${topic}`}
              </button>
            ))}
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
            {filteredArticles.map((article) => (
              <article
                key={article.id}
                className="bg-white rounded-xl overflow-hidden border border-slate-200 card-hover"
                data-testid={`article-card-${article.id}`}
              >
                {article.thumbnail_url && (
                  <img
                    src={article.thumbnail_url}
                    alt={article.title}
                    className="w-full h-[180px] object-cover"
                  />
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3 text-sm text-[#64748B] mb-3">
                    <span>{article.published_date}</span>
                    <span>•</span>
                    <span>{article.read_time}</span>
                  </div>
                  <h4 className="font-['Montserrat'] font-semibold text-[#2C3E50] mb-2 line-clamp-2">
                    {article.title}
                  </h4>
                  <p className="text-[#64748B] text-sm mb-4 line-clamp-2">
                    {article.description}
                  </p>

                  {/* Newsletter Subscribe Button */}
                  {article.is_newsletter && article.linkedin_newsletter_urn ? (
                    <a
                      href={`https://www.linkedin.com/build-relation/newsletter-follow?entityUrn=${article.linkedin_newsletter_urn}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 text-white bg-[#0A66C2] hover:bg-[#004182] px-6 py-2.5 rounded-full text-sm font-medium transition-colors duration-200"
                      style={{ textDecoration: 'none' }}
                    >
                      Subscribe on LinkedIn
                    </a>
                  ) : article.external_url ? (
                    <div className="space-y-2">
                      <a
                        href={article.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-[#3498DB] font-medium text-sm hover:gap-2 gap-1 transition-all duration-200"
                      >
                        {article.external_url.includes('drive.google.com') ? 'View PDF' : 'Read on Substack'} <ExternalLink size={14} />
                      </a>
                      {article.github_url && (
                        <a
                          href={article.github_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-[#2C3E50] font-medium text-sm hover:gap-2 gap-1 transition-all duration-200 ml-4"
                          title="View GitHub Repository"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                          </svg>
                          GitHub Repo
                        </a>
                      )}
                    </div>
                  ) : (
                    <button className="inline-flex items-center text-[#3498DB] font-medium text-sm hover:gap-2 gap-1 transition-all duration-200">
                      Read More <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#64748B]">No articles found for this topic. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        whitepaper={selectedWhitepaper}
      />
    </main>
  );
}
